import { useFeatureFlags } from "@/core/auth/hooks";
import type { I18nKey } from "@/core/i18n";
import { useAppSelector } from "@/hooks/redux";
import { DeviceSelectField } from "@/modules/device/components/DeviceSelectField";
import { useDeviceFleet } from "@/modules/device/hooks/useDeviceFleet";
import {
  selectRelaySessionReady,
  selectRelayUrl,
} from "@/modules/device/services/relaySlice";
import { DiscoverCameraApi } from "@/modules/discovery/components";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { Textarea } from "@/modules/shadcn/ui/textarea";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDetectCameras, type DetectionTarget } from "../../hooks/useDetectCameras";
import { validateCameraApiUrl } from "../../utils/validateCameraApiUrl";
import {
  CameraSelectField,
  type CameraOption,
  type CameraSource,
} from "./CameraSelectField";

interface ProjectDetailsFormProps {
  name: string;
  setName: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  deviceId: number | null;
  setDeviceId: (deviceId: number | null) => void;
  cameraApiUrl: string | null;
  setCameraApiUrl: (val: string) => void;
  cameraApiUrlError?: I18nKey<"project"> | null;
  onCameraApiUrlBlur?: () => void;
  selectedCameraMxid: string | null;
  setSelectedCameraMxid: (mxid: string | null) => void;
  /**
   * A local address already saved on this project while the organization may no
   * longer use one. Shown read-only, because hiding it leaves the owner of a
   * project that just stopped working with nothing to look at.
   */
  revokedLocalAddress?: string | null;
}

export const ProjectDetailsForm = ({
  name,
  setName,
  description,
  setDescription,
  deviceId,
  setDeviceId,
  cameraApiUrl,
  setCameraApiUrl,
  cameraApiUrlError,
  onCameraApiUrlBlur,
  selectedCameraMxid,
  setSelectedCameraMxid,
  revokedLocalAddress,
}: ProjectDetailsFormProps) => {
  const { t } = useTranslation(["project", "common"]);
  const { localCameraAccess } = useFeatureFlags();
  const { devices } = useDeviceFleet();
  const relayUrl = useAppSelector(selectRelayUrl);
  const relayReady = useAppSelector(selectRelaySessionReady);

  const selectedDevice = useMemo(
    () => (deviceId == null ? undefined : devices.find((d) => d.id === deviceId)),
    [devices, deviceId],
  );

  // Detect against the URL as currently typed, not the saved value. An invalid
  // URL yields no detection.
  const localUrl = useMemo(() => {
    const main = (cameraApiUrl ?? "").trim();
    if (!main) return null;
    return validateCameraApiUrl(main) ? null : main;
  }, [cameraApiUrl]);

  // A device is detected against through the relay — the same path the Capture
  // page will use, so a camera that can be picked here is one that will work.
  // Detecting against the typed address regardless of the selected device is
  // what used to make the picker unusable for a remote project: with the
  // address left empty, as the relay setup prescribes, there was nothing to
  // detect against and `cameraMxid` could never be set.
  const target: DetectionTarget | null =
    deviceId != null
      ? selectedDevice && relayReady && relayUrl
        ? { url: `${relayUrl}/d/${selectedDevice.deviceId}`, credentials: "include" }
        : null
      : localUrl
        ? { url: localUrl, credentials: "same-origin" }
        : null;

  const detection = useDetectCameras(target);

  // Without a live path to the device, its last heartbeat is the only camera
  // list available. Stale, so the field says so — but it beats a dead picker.
  const usingHeartbeat = deviceId != null && target === null;

  const cameras: CameraOption[] | null = useMemo(() => {
    if (usingHeartbeat) {
      return (
        selectedDevice?.cameras.map((c) => ({
          mxid: c.mxid,
          label: c.name ?? c.mxid,
        })) ?? null
      );
    }

    return (
      detection.cameras?.map((c) => ({ mxid: c.mxid, label: c.camera_name })) ?? null
    );
  }, [usingHeartbeat, selectedDevice, detection.cameras]);

  const source: CameraSource =
    deviceId != null
      ? { kind: "device", live: !usingHeartbeat }
      : localUrl
        ? { kind: "local" }
        : { kind: "none", reason: localCameraAccess ? "no-url" : "no-device" };

  return (
    <div className="relative flex flex-col gap-8">
      <h5 className="text-xl font-semibold">{t("details.title")}</h5>

      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="projectName" className="font-semibold">
            {t("details.nameLabel")}
          </Label>
          <Input
            id="projectName"
            placeholder={t("details.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="projectDescription" className="font-semibold">
            {t("details.descriptionLabel")}
          </Label>
          <Textarea
            id="projectDescription"
            placeholder={t("details.descriptionPlaceholder")}
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <DeviceSelectField value={deviceId} onChange={setDeviceId} />

        {/* Only under the flag, and only in local mode. A direct address
            bypasses the relay, so it is unauthenticated and unencrypted, and
            nothing checks the camera belongs to this organization — the API
            refuses to store one without the flag, and this keeps the UI from
            offering what would be rejected. It is also hidden once a device is
            chosen, because the two are alternatives: saving a device clears the
            address. */}
        {localCameraAccess && deviceId === null && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cameraApiUrl" className="font-semibold">
              {t("details.cameraApiUrl")}
            </Label>
            <div className="flex flex-row gap-2">
              <Input
                id="cameraApiUrl"
                className="flex-1"
                // eslint-disable-next-line i18next/no-literal-string -- example address, not prose
                placeholder="https://robopipe-1.local"
                value={cameraApiUrl ?? ""}
                aria-invalid={!!cameraApiUrlError}
                onChange={(e) => setCameraApiUrl(e.target.value)}
                onBlur={onCameraApiUrlBlur}
              />
              <DiscoverCameraApi onSelect={(url) => setCameraApiUrl(url)} />
            </div>
            {cameraApiUrlError && (
              <p className="text-xs text-destructive">{t(cameraApiUrlError)}</p>
            )}
          </div>
        )}

        {!localCameraAccess && revokedLocalAddress && (
          <div className="flex flex-col gap-1.5">
            <Label className="font-semibold text-black/45">
              {t("details.cameraApiUrl")}
            </Label>
            <p className="rounded-md bg-black/5 px-3 py-2 font-mono text-xs text-black/45">
              {revokedLocalAddress}
            </p>
            <p className="text-xs text-black/50">
              {t("details.localAccessDisabled")}
            </p>
          </div>
        )}

        <CameraSelectField
          cameras={cameras}
          source={source}
          isLoading={detection.isLoading}
          isError={detection.isError}
          onRetry={detection.retry}
          value={selectedCameraMxid}
          onChange={setSelectedCameraMxid}
        />
      </div>
    </div>
  );
};
